import React, { useMemo, useState, useEffect } from 'react';
import { useCollection } from '@nandorojo/swr-firestore';
import InventoryItem from './InventoryItem';
import styled from 'styled-components';
import { IPhotoCard } from '@typings/photocard/photocard.interface';
import InventoryDetailModal from './InventoryDetailModal';
import { CollectionPath } from '@typings/collections.enum';
import { getPathKey } from '@utils/getPath';
import { IinventoryItem, IstoreItem } from '@typings/store/storeItem.interface';
import { scrollbarStyle } from '@styles/variants/scrollbar';
import ViewToggleTab, {
  ViewOption,
} from '@components/dataDisplay/tab/ViewToggleTab/ViewToggleTab';
import { ITEM_FUNCTION_TPYE } from '@typings/store/storeItemFunctionType';
import { masterSet } from '@masterSetting/masterSet/masterSetting';

const ControlSection = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const TabsScroller = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;

  & > * {
    flex: 0 0 auto; /* 줄바꿈 방지, 가로 나열 */
  }

  &::-webkit-scrollbar {
    display: none;
    height: 0;
  }
`;

const ListWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: stretch;
  position: relative;
  overflow-y: auto;
  ${scrollbarStyle}
`;

const InventoryListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

// 카테고리 타입 정의
type CategoryType =
  | 'all'
  | 'myroom'
  | 'gift'
  | 'skill_equipment'
  | 'pet'
  | 'investigation';

// 정렬 타입 정의
type SortType = 'latest' | 'alphabetical' | 'oldest';

// 확장된 인벤토리 아이템 타입 (스토어 정보 포함)
type EnhancedInventoryItem = IinventoryItem & {
  storeItemData?: IstoreItem;
  createdAt?: Date; // 인벤토리 아이템의 생성 시점
  title?: string; // 호환성을 위한 title 필드
};

const sortOptions = [
  { key: 'latest', value: '최신순' },
  { key: 'alphabetical', value: '가나다순' },
  { key: 'oldest', value: '오래된 순' },
];

type Props = {
  photocardData?: IPhotoCard;
  collectionPath?: string;
  photocardID?: string;
  defaultCategory?: CategoryType; // 초기 카테고리 설정
  showOnlyCategory?: CategoryType; // 특정 카테고리만 표시
  listStyle?: React.CSSProperties;
};

function InventoryList({
  photocardData,
  collectionPath,
  photocardID,
  defaultCategory,
  showOnlyCategory,
  listStyle,
}: Props) {
  const _collectionPath = collectionPath || getPathKey(1);
  const _photocardID = photocardID || getPathKey(2);

  // showOnlyCategory가 있으면 그것을 우선시, 없으면 defaultCategory 사용
  const initialCategory = showOnlyCategory || defaultCategory || 'all';
  const [currentCategory, setCurrentCategory] =
    useState<CategoryType>(initialCategory);
  const [sortType, setSortType] = useState<SortType>('latest');
  const [enhancedItems, setEnhancedItems] = useState<EnhancedInventoryItem[]>(
    [],
  );

  // 인벤토리 아이템 가져오기
  const { data: inventoryList, error } = useCollection<IinventoryItem>(
    `${_collectionPath}/${_photocardID}/inventory`,
    { listen: true, orderBy: ['createdAt', 'desc'] },
  );

  // 스토어 아이템 전체 가져오기 (한 번만 호출)
  const { data: storeItems } = useCollection<IstoreItem>(CollectionPath.STORE, {
    listen: true,
  });

  // 인벤토리 아이템과 스토어 아이템 매칭
  useEffect(() => {
    if (!inventoryList || !storeItems) {
      setEnhancedItems([]);
      return;
    }

    // 스토어 아이템 맵 생성 (O(1) 조회를 위해)
    const storeItemMap = new Map<string, IstoreItem>();
    storeItems.forEach((item) => {
      if (item.id) {
        storeItemMap.set(item.id, item);
      }
    });

    // 인벤토리 아이템에 스토어 정보 추가
    const enhanced = inventoryList.map((inventoryItem) => ({
      ...inventoryItem,
      storeItemData: storeItemMap.get(inventoryItem.originItemID || ''),
    }));

    setEnhancedItems(enhanced);
  }, [inventoryList, storeItems]);

  // 카테고리별 필터링 함수 (하이브리드 방식)
  const filterByCategory = (
    items: EnhancedInventoryItem[],
    category: CategoryType,
  ) => {
    if (!items) return [];

    switch (category) {
      case 'all':
        return items;
      case 'myroom':
        const myroomItems = items.filter((item) => {
          // 🔥 하이브리드: 복사된 필드 우선, 없으면 스토어 데이터 사용
          const functionType =
            item.functionType || item.storeItemData?.functionType;
          return functionType === ITEM_FUNCTION_TPYE.MYROOM;
        });
        return myroomItems;
      case 'gift':
        const giftItems = items.filter(
          (item) => item.isPresent === true || !!item.giftingCharID,
        );
        return giftItems;
      case 'skill_equipment':
        const skillEquipItems = items.filter((item) => {
          const functionType =
            item.functionType || item.storeItemData?.functionType;
          return (
            functionType === ITEM_FUNCTION_TPYE.SKILL ||
            functionType === ITEM_FUNCTION_TPYE.EQUIPMENT
          );
        });
        return skillEquipItems;
      case 'pet':
        const petItems = items.filter((item) => {
          const functionType =
            item.functionType || item.storeItemData?.functionType;
          return functionType === ITEM_FUNCTION_TPYE.PET;
        });
        return petItems;
      case 'investigation':
        const investigationItems = items.filter((item) => {
          const functionType =
            item.functionType || item.storeItemData?.functionType;
          return functionType === ITEM_FUNCTION_TPYE.INVESTIGATION;
        });
        return investigationItems;
      default:
        return items;
    }
  };

  // 정렬 함수 (개선된 버전)
  const sortItems = (items: EnhancedInventoryItem[], sort: SortType) => {
    if (!items) return [];

    const sortedItems = [...items];

    switch (sort) {
      case 'latest':
        return sortedItems.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateB - dateA;
        });
      case 'alphabetical':
        return sortedItems.sort((a, b) => {
          const titleA = a.storeItemData?.title || a.title || '';
          const titleB = b.storeItemData?.title || b.title || '';
          return titleA.localeCompare(titleB, 'ko');
        });
      case 'oldest':
        return sortedItems.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateA - dateB;
        });
      default:
        return sortedItems;
    }
  };

  // 필터링 및 정렬된 아이템 목록
  const processedItems = useMemo(() => {
    const filtered = filterByCategory(enhancedItems, currentCategory);
    const sorted = sortItems(filtered, sortType);

    return sorted;
  }, [enhancedItems, currentCategory, sortType]);

  // 각 카테고리별 아이템 개수 계산
  const getCategoryCount = (category: CategoryType) => {
    const count = filterByCategory(enhancedItems, category).length;
    return count;
  };

  // ViewToggleTab 옵션 설정
  const allViewOptions: ViewOption[] = [
    {
      value: 'all',
      label: `전체 ${getCategoryCount('all')}`,
      isShow: true,
      drawView: null,
    },
    {
      value: 'myroom',
      label: `마이룸 ${getCategoryCount('myroom')}`,
      isShow: masterSet.stickerDoc || false,
      drawView: null,
    },
    {
      value: 'gift',
      label: `선물 ${getCategoryCount('gift')}`,
      isShow: true,
      drawView: null,
    },
    // {
    //   value: 'skill_equipment',
    //   label: `스킬/장비 ${getCategoryCount('skill_equipment')}`,
    //   isShow: masterSet.status || false,
    //   drawView: null,
    // },
    {
      value: 'investigation',
      label: `조사 ${getCategoryCount('investigation')}`,
      isShow: masterSet.explorationSystem || false,
      drawView: null,
    },
    {
      value: 'pet',
      label: `펫 ${getCategoryCount('pet')}`,
      isShow: masterSet.petSystem || false,
      drawView: null,
    },
  ];

  // 특정 카테고리만 표시하는 경우 필터링
  const viewOptions = showOnlyCategory
    ? allViewOptions.filter((option) => option.value === showOnlyCategory)
    : allViewOptions;

  return (
    <InventoryListContainer>
      <ControlSection>
        <TabsScroller>
          <ViewToggleTab
            viewOptions={viewOptions}
            defaultViewType={initialCategory}
            customSetViewType={setCurrentCategory}
            style={{ margin: 0 }}
            buttonStyle={{ fontSize: '12px', padding: '4px 8px' }}
          />
        </TabsScroller>
      </ControlSection>

      <ListWrapper style={{ ...listStyle }}>
        {processedItems && processedItems.length > 0 ? (
          processedItems.map((item, i) => {
            return (
              <InventoryItem
                key={`${item.id}-${i}`}
                originItemID={item.originItemID || ''}
                data={item}
                photocardData={photocardData}
                modal={<InventoryDetailModal />}
                collectionPath={CollectionPath.STORE}
              />
            );
          })
        ) : (
          <div
            style={{
              width: '100%',
              textAlign: 'center',
              color: 'var(--subTextColor)',
              padding: '40px 0',
              fontSize: '13px',
            }}
          >
            {currentCategory === 'all'
              ? '인벤토리가 비어있습니다.'
              : '해당 카테고리의 아이템이 없습니다.'}
          </div>
        )}
      </ListWrapper>
    </InventoryListContainer>
  );
}

export default InventoryList;
